import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { RouteUtils } from "utilities/route-utils";

interface PageFetchAllBlocksQueryParams {
    fetchAllBlocks?: boolean | string;
    [key: string]: unknown;
}

const isFetchAllBlocksValue = (value: unknown): boolean =>
    value === true || value === "true" || value === "";

/**
 * Checks for the "fetchAllBlocks" query parameter in the URL and exposes a
 * refresh key for each subsequent occurrence.
 * @example `?fetchAllBlocks=true`
 * @example `?fetchAllBlocks`
 */
export interface PageFetchAllBlocksQueryParamsState {
    fetchAllBlocks: boolean;
    refreshKey: number;
}

export const usePageFetchAllBlocksQueryParams =
    (): PageFetchAllBlocksQueryParamsState => {
        const { pathname, search } = useLocation();
        const { replace } = useHistory();
        const [fetchAllBlocks, setFetchAllBlocks] = useState(() => {
            const queryParams =
                RouteUtils.queryStringToObject<PageFetchAllBlocksQueryParams>(
                    search
                );

            return isFetchAllBlocksValue(queryParams.fetchAllBlocks);
        });
        const hasInitialized = useRef(false);
        const [refreshKey, setRefreshKey] = useState(0);

        useEffect(() => {
            const queryParams =
                RouteUtils.queryStringToObject<PageFetchAllBlocksQueryParams>(
                    search
                );
            const { fetchAllBlocks: queryFetchAllBlocks, ...otherParams } =
                queryParams;

            if (isFetchAllBlocksValue(queryFetchAllBlocks)) {
                setFetchAllBlocks(true);
                if (hasInitialized.current) {
                    setRefreshKey((currentKey) => currentKey + 1);
                }
                replace(
                    RouteUtils.appendQueryParams(pathname, {
                        ...otherParams,
                    })
                );
            }

            hasInitialized.current = true;
        }, [pathname, replace, search]);

        return { fetchAllBlocks, refreshKey };
    };
